/* One transfer row on the File destination.
 *
 * The Figma pins the card at 328px inside a 360px frame. Our layouts are fluid
 * 360–400 (redesign plan D5), so the card fills its container instead — the
 * fixed width would leave a ragged edge at 400px.
 *
 * The design expresses padding and gaps with RADIUS tokens (px-[--radius/16],
 * gap-[--radius/8]) even though the spacing scale exists. Mapped to
 * --spacing-* here so corner radii and layout spacing stay independent. */

.card {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-12);
  padding: var(--spacing-12);
  border-radius: var(--radius-12);
  background: var(--bg-secondary);
}

.head {
  display: flex;
  gap: var(--spacing-12);
  align-items: flex-start;
}

.iconBox {
  display: flex;
  align-items: center;
  justify-content: center;
  /* Fixed: the icon plate must not shrink when a long filename pushes on it. */
  flex: none;
  width: 40px;
  height: 40px;
  border-radius: var(--radius-8);
  background: var(--bg-tertiary);
}

.fileIcon {
  width: 20px;
  height: 20px;
  color: var(--text-secondary);
}

.meta {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
  /* min-width: 0 is what actually lets the filename ellipsis engage — without
   * it this flex child refuses to shrink below its content and the row
   * overflows the card instead of truncating. */
  flex: 1;
  min-width: 0;
}

.fileName {
  margin: 0;
  font: var(--text-body-semibold);
  color: var(--text-primary);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.metaRow {
  display: flex;
  gap: var(--spacing-8);
  align-items: center;
  /* Last-resort safety net, not the plan. The row is sized to fit on ONE line
   * in the narrowest supported panel (size + percentage + transport chip) —
   * which is why the transferring status is a bare percentage carrying no verb;
   * see the `fileTransferTransferringPct` description. If this starts wrapping
   * again, shorten the content rather than accept the second line: an orphaned
   * "DIRECT" reads as a stray label rather than part of the row. */
  flex-wrap: wrap;
}

.status {
  font: var(--text-micro-medium);
  letter-spacing: var(--text-xs-tracking);
  /* The status wraps as a unit or not at all — never broken mid-token. */
  white-space: nowrap;
}

/* In-flight: the transfer accent. */
.accent {
  color: var(--transfer-accent);
}

/* Completed: the row is history, so it stops competing with live transfers. */
.muted {
  color: var(--text-tertiary);
}

/* Failed or cancelled. */
.negative {
  color: var(--text-negative);
}

/* Route indicator: which path the bytes take. Quieter than the status — it is
 * provenance, not progress. Direct picks up the transfer accent because it is
 * the faster route worth noticing; relay stays tertiary. Deliberately NOT a
 * warning colour: both routes are end-to-end encrypted, and colouring the
 * server path as a caution would misrepresent the product. */
.transport {
  display: inline-flex;
  align-items: center;
  gap: var(--spacing-2);
  font: var(--text-micro-medium);
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--text-tertiary);
  white-space: nowrap;
}

.transportDirect {
  color: var(--transfer-accent);
}

.transportIcon {
  width: 12px;
  height: 12px;
  /* The glyph inherits the chip's colour so the two never disagree. */
  color: currentColor;
  flex: none;
}

.actions {
  display: flex;
  gap: var(--spacing-8);
}

.accept,
.reject {
  flex: 1;
  min-width: 0;
  padding: var(--spacing-4) var(--spacing-8);
  border: none;
  border-radius: var(--radius-12);
  font: var(--text-micro-medium);
  cursor: pointer;
}

/* Accept carries the emphasis. The Figma styles both buttons identically,
 * which leaves the affirmative action indistinguishable from the destructive
 * one on a row whose whole purpose is that choice. */
.accept {
  background: var(--transfer-accent);
  /* Fixed dark ink in BOTH themes, not --bg-primary. On light, #ffffff on
   * green-600 is 3.77:1 — below AA for 12px text. #171717 gives 4.76:1 on
   * green-600 and 11.8:1 on green-300, and "wash stays themed, ink stays
   * fixed dark" is the pattern --brand-gradient already establishes. */
  color: var(--brand-gradient-ink);
}

.reject {
  background: var(--button-secondary-bg-default);
  color: var(--text-primary);
}

.accept:hover,
.reject:hover {
  filter: brightness(1.08);
}

.accept:focus-visible,
.reject:focus-visible {
  outline: 2px solid var(--transfer-accent);
  outline-offset: 2px;
}
